import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import React, { useEffect, useState } from 'react';
import Box from '../Box';
import muiFallbackIconMap from './muiFallbackIconMap';
import './MuiFaIcon.scss';

type MuiFaIconProps = {
  name: string;
  className?: string;
  sx?: SxProps<Theme>;
  style?: CSSProperties;
  fontSize?: SvgIconProps['fontSize'];
};

let cachedFontAwesomeAvailable: boolean | null = null;

async function checkFontAwesomeAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  const fontSet = (document as any).fonts;

  if (!fontSet) {
    return false;
  }

  try {
    await fontSet.ready;

    let hasLoadedFontAwesomeFont = false;

    fontSet.forEach((fontFace: FontFace) => {
      const family = fontFace.family.replace(/"/g, '').toLowerCase();

      if (family.includes('font awesome') && fontFace.status === 'loaded') {
        hasLoadedFontAwesomeFont = true;
      }
    });

    return hasLoadedFontAwesomeFont;
  } catch {
    return false;
  }
}

export default function MuiFaIcon({
  name,
  className = '',
  sx,
  style,
  fontSize,
}: MuiFaIconProps) {
  const MuiFallbackIcon = muiFallbackIconMap[name];
  const shouldSpin = name.includes('fa-spin');

  const [fontAwesomeAvailable, setFontAwesomeAvailable] = useState(() => {
    return cachedFontAwesomeAvailable === true;
  });

  useEffect(() => {
    let cancelled = false;

    if (cachedFontAwesomeAvailable !== null) {
      setFontAwesomeAvailable(cachedFontAwesomeAvailable);
      return;
    }

    checkFontAwesomeAvailable().then((available) => {
      cachedFontAwesomeAvailable = available;

      if (!cancelled) {
        setFontAwesomeAvailable(available);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!fontAwesomeAvailable && MuiFallbackIcon) {
    return (
      <MuiFallbackIcon
        className={`mui-fa-icon ${
          shouldSpin ? 'fa-spin' : ''
        } ${className}`.trim()}
        sx={sx}
        style={style}
        fontSize={fontSize}
      />
    );
  }

  if (!fontAwesomeAvailable) {
    return null;
  }

  return (
    <Box
      component="i"
      className={`mui-fa-icon ${name} ${className}`.trim()}
      sx={sx}
      style={style}
    />
  );
}
